import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useRouter, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Plus, Wand2, FileDown, CheckCircle2, Archive, Trash2, RotateCcw, Eye, Sparkles, Package } from "lucide-react";
import JSZip from "jszip";
import { AccessDenied } from "@/components/AccessDenied";
import {
  MANUAL_LANGS,
  type ManualLang,
  type ManualStep,
  listManualsAdmin,
  upsertManual,
  translateManual,
  setManualStatus,
  deleteManual,
  saveManualFile,
  getManualSignedUrl,
  suggestManualStructure,
} from "@/lib/admin-manuals.functions";
import { generateManualPdf, uploadManualPdf } from "@/lib/manual-pdf";
import { captureManualScreenshots, listManualScreenshots, getScreenshotSignedUrls } from "@/lib/admin-manual-screenshots.functions";
import { ManualAuditPanel } from "@/components/admin/ManualAuditPanel";
import { ManualsErrorPage } from "@/components/admin/ManualsErrorPage";
import { ManualPdfThumb, PdfAllPages } from "@/components/admin/ManualPdfThumb";

export const Route = createFileRoute("/admin/manualy")({
  component: ManualsPage,
  errorComponent: ManualsErrorPage,
  notFoundComponent: () => (
    <ManualsErrorPage
      error={new Error("Stránka nenalezena")}
      reset={() => {}}
      code="MANUALS_404"
    />
  ),
});

type Row = {
  id: string;
  slug: string;
  title_i18n: Record<string, string>;
  description_i18n: Record<string, string>;
  content_i18n: Record<string, ManualStep[]>;
  audience: "public" | "vakant_owners";
  role_scope: string;
  status: "draft" | "pending_review" | "approved" | "archived";
  sort_order: number;
  approved_at: string | null;
  version?: number;
  content_hash?: string | null;
  files?: Array<{ id: string; lang: string; storage_path: string; file_name: string; charset_ok: boolean; charset_issues: any }>;
};

const EMPTY: Omit<Row, "id" | "approved_at" | "files"> = {
  slug: "",
  title_i18n: {},
  description_i18n: {},
  content_i18n: { cs: [{ title: "", body: "" }] },
  audience: "public",
  role_scope: "any",
  status: "draft",
  sort_order: 100,
};

function ManualsPage() {
  const { hasRole, loading } = useAuth();
  const isAdmin = hasRole("admin");
  const qc = useQueryClient();

  const list = useServerFn(listManualsAdmin);
  const save = useServerFn(upsertManual);
  const translate = useServerFn(translateManual);
  const setStatus = useServerFn(setManualStatus);
  const removeM = useServerFn(deleteManual);
  const saveFile = useServerFn(saveManualFile);
  const signManual = useServerFn(getManualSignedUrl);

  const [editing, setEditing] = useState<Row | null>(null);
  const [sourceLang, setSourceLang] = useState<ManualLang>("cs");
  const [translating, setTranslating] = useState(false);
  const [savingRow, setSavingRow] = useState(false);
  const [generating, setGenerating] = useState<string | null>(null);
  const [zipping, setZipping] = useState<string | null>(null);
  const [capturing, setCapturing] = useState<string | null>(null);
  const capture = useServerFn(captureManualScreenshots);
  const listShots = useServerFn(listManualScreenshots);
  const signShots = useServerFn(getScreenshotSignedUrls);
  const [previewFor, setPreviewFor] = useState<Row | null>(null);
  const [previewLang, setPreviewLang] = useState<ManualLang>("cs");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewUsesStoredFile, setPreviewUsesStoredFile] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewMeta, setPreviewMeta] = useState<{ ok: boolean; missing: string[] } | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestTopic, setSuggestTopic] = useState("");
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestStepsHint, setSuggestStepsHint] = useState(8);
  const suggest = useServerFn(suggestManualStructure);

  type DiagStage = "load" | "generate" | "upload" | "save-metadata" | "download";
  type DiagEvent = { id: string; at: string; stage: DiagStage; lang?: ManualLang; ok: boolean; message: string; detail?: string };
  const [diag, setDiag] = useState<DiagEvent[]>([]);
  const [diagOpen, setDiagOpen] = useState(false);
  const pushDiag = (e: Omit<DiagEvent, "id" | "at">) => {
    const ev: DiagEvent = { ...e, id: Math.random().toString(36).slice(2, 9), at: new Date().toISOString() };
    setDiag((prev) => [ev, ...prev].slice(0, 50));
    setDiagOpen(true);
    // eslint-disable-next-line no-console
    console.info(`[manualy diag] ${e.stage}${e.lang ? `/${e.lang}` : ""} ${e.ok ? "OK" : "FAIL"} — ${e.message}`, e.detail ?? "");
    return ev;
  };
  const clearDiag = () => setDiag([]);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ["admin", "manuals"],
    queryFn: () => list(),
    enabled: isAdmin,
  });

  useEffect(() => {
    if (loadError) pushDiag({ stage: "load", ok: false, message: "Načtení seznamu manuálů selhalo", detail: (loadError as any)?.message ?? String(loadError) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadError]);


  const rows = ((data ?? []) as unknown) as Row[];

  const startNew = () => setEditing({ ...EMPTY, id: "", approved_at: null } as Row);


  const currentTitle = (r: Row) => r.title_i18n?.cs || r.title_i18n?.en || r.slug;

  const doSuggest = async () => {
    if (!suggestTopic.trim()) {
      toast.error("Popiš krátce, co má manuál pokrýt.");
      return;
    }
    setSuggestBusy(true);
    try {
      const audience: "public" | "vakant_owners" = editing?.audience ?? "public";
      const res = await suggest({
        data: {
          source_lang: sourceLang,
          topic: suggestTopic.trim(),
          audience,
          hint_steps: suggestStepsHint,
        },
      });
      const base: Row = editing ?? ({ ...EMPTY, id: "", approved_at: null } as Row);
      setEditing({
        ...base,
        slug: base.slug || (res.recommended_slug ?? ""),
        title_i18n: { ...(base.title_i18n ?? {}), [sourceLang]: res.title },
        description_i18n: { ...(base.description_i18n ?? {}), [sourceLang]: res.description },
        content_i18n: { ...(base.content_i18n ?? {}), [sourceLang]: res.steps },
      });
      toast.success(`Návrh připraven: ${res.steps.length} kroků.`);
      setSuggestOpen(false);
    } catch (e: any) {
      toast.error(e.message ?? String(e));
    } finally {
      setSuggestBusy(false);
    }
  };

  const doTranslate = async () => {
    if (!editing) return;
    const steps = editing.content_i18n?.[sourceLang] ?? [];
    if (!editing.title_i18n?.[sourceLang] || steps.length === 0) {
      toast.error("Vyplňte titulek a alespoň jeden krok ve zdrojovém jazyce.");
      return;
    }
    setTranslating(true);
    try {
      const res = await translate({
        data: {
          source_lang: sourceLang,
          title: editing.title_i18n[sourceLang]!,
          description: editing.description_i18n?.[sourceLang] ?? "",
          steps,
        },
      });
      setEditing({
        ...editing,
        title_i18n: { ...editing.title_i18n, ...res.title_i18n },
        description_i18n: { ...editing.description_i18n, ...res.description_i18n },
        content_i18n: { ...editing.content_i18n, ...res.content_i18n },
      });
      toast.success("Přeloženo do všech jazyků.");
    } catch (e: any) {
      toast.error(e.message ?? String(e));
    } finally {
      setTranslating(false);
    }
  };

  const doSave = async () => {
    if (!editing) return;
    if (!editing.slug || !editing.title_i18n?.[sourceLang]) {
      toast.error("Slug a titulek jsou povinné.");
      return;
    }
    setSavingRow(true);
    try {
      const res = await save({
        data: {
          id: editing.id || undefined,
          slug: editing.slug,
          audience: editing.audience,
          role_scope: editing.role_scope,
          sort_order: editing.sort_order,
          title_i18n: editing.title_i18n as Record<string, string>,
          description_i18n: editing.description_i18n as Record<string, string>,
          content_i18n: editing.content_i18n as Record<string, ManualStep[]>,
        },
      });
      toast.success("Uloženo.");
      setEditing({ ...editing, id: res.id });
      await qc.invalidateQueries({ queryKey: ["admin", "manuals"] });
    } catch (e: any) {
      toast.error(e.message ?? String(e));
    } finally {
      setSavingRow(false);
    }
  };

  /** Fetch step image data URLs for a manual+lang, keyed by step index. */
  const fetchStepImages = async (manualId: string, lang: ManualLang): Promise<Record<number, string>> => {
    try {
      const rows = await listShots({ data: { manual_id: manualId } });
      const forLang = (rows ?? []).filter((r: any) => r.lang === lang);
      if (forLang.length === 0) return {};
      const signed = await signShots({ data: { paths: forLang.map((r: any) => r.storage_path) } });
      const out: Record<number, string> = {};
      await Promise.all(forLang.map(async (r: any) => {
        const url = signed[r.storage_path];
        if (!url) return;
        try {
          const resp = await fetch(url);
          if (!resp.ok) return;
          const buf = new Uint8Array(await resp.arrayBuffer());
          let bin = ""; const chunk = 0x8000;
          for (let i = 0; i < buf.length; i += chunk) bin += String.fromCharCode.apply(null, buf.subarray(i, i + chunk) as any);
          out[r.step_index] = `data:image/png;base64,${btoa(bin)}`;
        } catch { /* skip */ }
      }));
      return out;
    } catch { return {}; }
  };

  const doCapture = async (r: Row) => {
    setCapturing(r.id);
    try {
      const res = await capture({ data: { manual_id: r.id, only_missing: false } });
      const fail = res.total - res.ok;
      pushDiag({ stage: "generate", ok: fail === 0, message: `Screenshoty: ${res.ok}/${res.total} OK${fail ? `, ${fail} selhalo` : ""}`, detail: fail ? JSON.stringify(res.results.filter((x: any) => !x.ok).slice(0, 6)) : undefined });
      if (fail === 0) toast.success(`Zachyceno ${res.ok} screenshotů.`);
      else toast.warning(`Zachyceno ${res.ok}/${res.total}. Podívejte se do diagnostiky.`);
    } catch (e: any) {
      toast.error(e?.message ?? String(e));
    } finally {
      setCapturing(null);
    }
  };

  const generateAllPdfs = async (r: Row) => {
    setGenerating(r.id);
    try {
      const langs = Object.keys(r.content_i18n).filter((l) => MANUAL_LANGS.includes(l as ManualLang)) as ManualLang[];
      if (langs.length === 0) {
        pushDiag({ stage: "generate", ok: false, message: "Manuál nemá žádný obsah k vygenerování." });
        toast.error("Manuál nemá žádný obsah k vygenerování.");
        return;
      }
      let missingTotal = 0;
      for (const lang of langs) {
        const steps = r.content_i18n[lang] ?? [];
        let blob: Blob, charset_ok: boolean, missing: string[];
        // Stage: generování PDF
        const stepImages = await fetchStepImages(r.id, lang);
        try {
          const res = await generateManualPdf({
            lang,
            title: r.title_i18n?.[lang] ?? r.slug,
            description: r.description_i18n?.[lang] ?? "",
            steps,
            stepImages,
          });
          blob = res.blob; charset_ok = res.charset_ok; missing = res.missing;
          pushDiag({ stage: "generate", lang, ok: true, message: `PDF vygenerováno (${Math.round(blob.size / 1024)} kB)${charset_ok ? "" : `, chybí ${missing.length} znaků`}` });
        } catch (err: any) {
          pushDiag({ stage: "generate", lang, ok: false, message: "Generování PDF selhalo", detail: err?.message ?? String(err) });
          throw err;
        }
        if (!charset_ok) missingTotal += missing.length;
        // Stage: upload/stream do storage
        let up: { storage_path: string; file_name: string; byte_size: number };
        try {
          up = await uploadManualPdf(r.id, lang, blob);
          pushDiag({ stage: "upload", lang, ok: true, message: `Nahráno do úložiště (${up.file_name}, ${Math.round(up.byte_size / 1024)} kB)` });
        } catch (err: any) {
          pushDiag({ stage: "upload", lang, ok: false, message: "Nahrání PDF do úložiště selhalo", detail: err?.message ?? String(err) });
          throw err;
        }
        // Stage: uložení metadat
        try {
          await saveFile({
            data: {
              manual_id: r.id,
              lang,
              storage_path: up.storage_path,
              file_name: up.file_name,
              byte_size: up.byte_size,
              charset_ok,
              charset_issues: charset_ok ? null : { missing_chars: missing },
            },
          });
          pushDiag({ stage: "save-metadata", lang, ok: true, message: "Metadata uložena." });
        } catch (err: any) {
          pushDiag({ stage: "save-metadata", lang, ok: false, message: "Uložení metadat selhalo", detail: err?.message ?? String(err) });
          throw err;
        }
      }
      toast.success(
        missingTotal === 0
          ? `Vygenerováno ${langs.length} PDF (znaková sada OK).`
          : `Vygenerováno ${langs.length} PDF. Pozor: ${missingTotal} znaků nemusí být plně čitelných.`,
      );
      await qc.invalidateQueries({ queryKey: ["admin", "manuals"] });
    } catch (e: any) {
      toast.error(e.message ?? String(e));
    } finally {
      setGenerating(null);
    }
  };

  /**
   * Vygeneruje PDF pro všechny jazyky manuálu v paměti a stáhne je jako jeden ZIP.
   * Neukládá do storage ani nemění metadata – slouží pro rychlou brand kontrolu.
   */
  const downloadAllAsZip = async (r: Row) => {
    setZipping(r.id);
    try {
      const langs = Object.keys(r.content_i18n).filter((l) => MANUAL_LANGS.includes(l as ManualLang)) as ManualLang[];
      if (langs.length === 0) {
        toast.error("Manuál nemá žádný obsah k vygenerování.");
        return;
      }
      const zip = new JSZip();
      const folder = zip.folder(`cerveza-international-${r.slug}`)!;
      let missingTotal = 0;
      const readme: string[] = [
        `cerveza-international – ${r.slug}`,
        `Vygenerováno: ${new Date().toISOString()}`,
        `Verze obsahu: v${r.version ?? 1}`,
        `Jazyky (${langs.length}): ${langs.join(", ")}`,
        "",
        "Soubory:",
      ];
      for (const lang of langs) {
        const steps = r.content_i18n[lang] ?? [];
        const stepImages = await fetchStepImages(r.id, lang);
        try {
          const res = await generateManualPdf({
            lang,
            title: r.title_i18n?.[lang] ?? r.slug,
            description: r.description_i18n?.[lang] ?? "",
            steps,
            stepImages,
          });
          const fileName = `cerveza-international-${r.slug}-${lang}.pdf`;
          folder.file(fileName, res.blob);
          readme.push(
            `  ${fileName}  (${Math.round(res.blob.size / 1024)} kB${res.charset_ok ? "" : `, ⚠ ${res.missing.length} nepokrytých znaků`})`,
          );
          if (!res.charset_ok) missingTotal += res.missing.length;
          pushDiag({ stage: "generate", lang, ok: true, message: `ZIP: PDF vygenerováno (${Math.round(res.blob.size / 1024)} kB)` });
        } catch (err: any) {
          readme.push(`  [FAIL] ${lang}: ${err?.message ?? String(err)}`);
          pushDiag({ stage: "generate", lang, ok: false, message: "ZIP: generování selhalo", detail: err?.message ?? String(err) });
        }
      }
      folder.file("README.txt", readme.join("\n"));
      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cerveza-international-${r.slug}-manuals.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      pushDiag({ stage: "download", ok: true, message: `ZIP připraven: ${langs.length} jazyků, ${Math.round(blob.size / 1024)} kB` });
      toast.success(
        missingTotal === 0
          ? `ZIP se stahuje: ${langs.length} PDF (znaková sada OK).`
          : `ZIP se stahuje: ${langs.length} PDF. ⚠ Nepokrytých znaků: ${missingTotal}.`,
      );
    } catch (e: any) {
      pushDiag({ stage: "download", ok: false, message: "Vytvoření ZIPu selhalo", detail: e?.message ?? String(e) });
      toast.error(e?.message ?? String(e));
    } finally {
      setZipping(null);
    }
  };



  const changeStatus = async (id: string, status: Row["status"]) => {
    try {
      await setStatus({ data: { id, status } });
      toast.success("Stav aktualizován.");
      await qc.invalidateQueries({ queryKey: ["admin", "manuals"] });
    } catch (e: any) { toast.error(e.message ?? String(e)); }
  };

  const remove = async (id: string) => {
    if (!confirm("Smazat manuál včetně všech PDF?")) return;
    try {
      await removeM({ data: { id } });
      toast.success("Smazáno.");
      if (editing?.id === id) setEditing(null);
      await qc.invalidateQueries({ queryKey: ["admin", "manuals"] });
    } catch (e: any) { toast.error(e.message ?? String(e)); }
  };

  const openPreview = (r: Row) => {
    const fileLangs = (r.files ?? []).map((f) => f.lang).filter((l) => MANUAL_LANGS.includes(l as ManualLang)) as ManualLang[];
    const contentLangs = Object.keys(r.content_i18n).filter((l) => MANUAL_LANGS.includes(l as ManualLang)) as ManualLang[];
    const langs = fileLangs.length > 0 ? fileLangs : contentLangs;
    if (langs.length === 0) { toast.error("Manuál nemá žádný obsah k náhledu."); return; }
    setPreviewFor(r);
    setPreviewLang((langs.includes(sourceLang) ? sourceLang : langs[0]) as ManualLang);
  };

  const closePreview = () => {
    setPreviewFor(null);
    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewMeta(null);
    setPreviewUsesStoredFile(false);
  };

  useEffect(() => {
    if (!previewFor) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    (async () => {
      setPreviewBusy(true);
      setPreviewMeta(null);
      setPreviewUsesStoredFile(false);
      try {
        const storedFile = previewFor.files?.find((f) => f.lang === previewLang) ?? null;
        if (storedFile) {
          const signed = await signManual({ data: { storage_path: storedFile.storage_path } });
          if (cancelled) return;
          setPreviewUrl((old) => { if (old?.startsWith("blob:")) URL.revokeObjectURL(old); return signed.url; });
          setPreviewMeta({ ok: storedFile.charset_ok, missing: storedFile.charset_issues?.missing ?? [] });
          setPreviewUsesStoredFile(true);
          pushDiag({ stage: "download", lang: previewLang, ok: true, message: `Náhled načten z uloženého PDF: ${storedFile.file_name}` });
          return;
        }

        const steps = previewFor.content_i18n[previewLang] ?? [];
        let blob: Blob, charset_ok: boolean, missing: string[];
        const stepImages = await fetchStepImages(previewFor.id, previewLang);
        try {
          const res = await generateManualPdf({
            lang: previewLang,
            title: previewFor.title_i18n?.[previewLang] ?? previewFor.slug,
            description: previewFor.description_i18n?.[previewLang] ?? "",
            steps,
            stepImages,
          });
          blob = res.blob; charset_ok = res.charset_ok; missing = res.missing;
          pushDiag({ stage: "generate", lang: previewLang, ok: true, message: `Náhled PDF vygenerován (${Math.round(blob.size / 1024)} kB)` });
        } catch (err: any) {
          pushDiag({ stage: "generate", lang: previewLang, ok: false, message: "Generování náhledu PDF selhalo", detail: err?.message ?? String(err) });
          throw err;
        }
        if (cancelled) return;
        try {
          createdUrl = URL.createObjectURL(blob);
          setPreviewUrl((old) => { if (old?.startsWith("blob:")) URL.revokeObjectURL(old); return createdUrl; });
          setPreviewMeta({ ok: charset_ok, missing });
        } catch (err: any) {
          pushDiag({ stage: "download", lang: previewLang, ok: false, message: "Vytvoření náhledového URL selhalo", detail: err?.message ?? String(err) });
          throw err;
        }
      } catch (e: any) {
        if (!cancelled) toast.error(e.message ?? String(e));
      } finally {
        if (!cancelled) setPreviewBusy(false);
      }
    })();

    return () => { cancelled = true; if (createdUrl) URL.revokeObjectURL(createdUrl); };
  }, [previewFor, previewLang]);

  if (loading) return <div className="p-6"><Loader2 className="animate-spin" /></div>;
  if (!isAdmin) return <AccessDenied title="Přístup pouze pro hlavního administrátora" requiredRole="admin" />;

  return (

    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl">Manuály (PDF)</h1>
          <p className="text-sm text-muted-foreground">
            Generujte krok-za-krokem manuály. Systém přeloží obsah do všech {MANUAL_LANGS.length} jazyků a po schválení
            zveřejní PDF v sekci Ke stažení. Znaková sada je kontrolována při generování.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="secondary" onClick={() => { setSuggestTopic(""); setSuggestOpen(true); }}>
            <Sparkles className="w-4 h-4 mr-1" /> Navrhnout AI
          </Button>
          <Button onClick={startNew}><Plus className="w-4 h-4 mr-1" /> Nový manuál</Button>
        </div>
      </div>

      <ManualAuditPanel />

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-sm flex items-center gap-2">
              Diagnostika manuálů
              <Badge variant="outline">{diag.length} událostí</Badge>
              {diag.some((d) => !d.ok) && <Badge variant="destructive">obsahuje chyby</Badge>}
            </CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setDiagOpen((v) => !v)}>{diagOpen ? "Skrýt" : "Zobrazit"}</Button>
              <Button size="sm" variant="ghost" onClick={clearDiag} disabled={diag.length === 0}>Vyčistit</Button>
            </div>
          </div>
        </CardHeader>
        {diagOpen && (
          <CardContent className="pt-0">
            <p className="text-xs text-muted-foreground mb-2">
              Rozlišuje fázi selhání: <code>load</code> (načtení manuálu z DB), <code>generate</code> (tvorba PDF v prohlížeči),
              {" "}<code>upload</code> (nahrání/streamování do úložiště), <code>save-metadata</code> (uložení záznamu o souboru),
              {" "}<code>download</code> (příprava URL pro stažení/náhled).
            </p>
            {diag.length === 0 ? (
              <p className="text-xs text-muted-foreground">Zatím žádné diagnostické události.</p>
            ) : (
              <ul className="space-y-1 max-h-64 overflow-auto text-xs font-mono">
                {diag.map((e) => (
                  <li key={e.id} className={`flex gap-2 items-start rounded px-2 py-1 ${e.ok ? "bg-emerald-500/5" : "bg-destructive/10"}`}>
                    <span className="text-muted-foreground shrink-0">{e.at.slice(11, 19)}</span>
                    <Badge variant={e.ok ? "secondary" : "destructive"} className="shrink-0">{e.stage}{e.lang ? `/${e.lang}` : ""}</Badge>
                    <span className="min-w-0 break-words">
                      {e.message}
                      {e.detail && <span className="block text-muted-foreground">{e.detail}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        )}
      </Card>




      {isLoading ? <Loader2 className="animate-spin" /> : (
        <div className="grid gap-3">
          {rows.map((r) => (
            <Card key={r.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start gap-3 flex-wrap">
                  <ManualPdfThumb files={r.files} title={currentTitle(r)} preferLang={sourceLang} />
                  <div className="flex items-start justify-between gap-3 flex-wrap flex-1 min-w-0">
                  <div className="min-w-0">
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                      {currentTitle(r)}
                      <Badge variant={r.status === "approved" ? "default" : "secondary"}>{r.status}</Badge>
                      <Badge variant="outline">{r.audience === "vakant_owners" ? "Jen majitelé Vacantů" : "Veřejné"}</Badge>
                      <Badge variant="outline">{(r.files?.length ?? 0)} / {MANUAL_LANGS.length} PDF</Badge>
                      <Badge variant="outline" title="Verze obsahu – roste při každé úpravě">v{r.version ?? 1}</Badge>
                    </CardTitle>
                    <p className="text-xs text-muted-foreground truncate">/{r.slug}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => setEditing(r)}>Upravit</Button>
                    <Button size="sm" variant="outline" onClick={() => openPreview(r)}>
                      <Eye className="w-4 h-4 mr-1" /> Náhled
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => generateAllPdfs(r)} disabled={generating === r.id}>
                      {generating === r.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
                      Vygenerovat PDF
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadAllAsZip(r)}
                      disabled={zipping === r.id}
                      title="Stáhne všechny jazykové verze jako jeden ZIP pro rychlou brand kontrolu"
                    >
                      {zipping === r.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Package className="w-4 h-4 mr-1" />}
                      Stáhnout ZIP
                    </Button>
                    {r.status !== "approved" ? (
                      <Button size="sm" onClick={() => changeStatus(r.id, "approved")}
                        disabled={(r.files?.length ?? 0) === 0}>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Schválit
                      </Button>
                    ) : (
                      <Button size="sm" variant="secondary" onClick={() => changeStatus(r.id, "draft")}>
                        <RotateCcw className="w-4 h-4 mr-1" /> Vrátit do konceptu
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => changeStatus(r.id, "archived")}><Archive className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                  </div>
                </div>
              </CardHeader>
              {r.files?.some((f) => !f.charset_ok) && (
                <CardContent className="pt-0">
                  <div className="text-xs text-amber-600 dark:text-amber-400">
                    Upozornění: PDF obsahují znaky mimo podporovanou znakovou sadu ({r.files.filter(f => !f.charset_ok).map(f => f.lang).join(", ")}).
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
          {rows.length === 0 && <p className="text-sm text-muted-foreground">Zatím žádné manuály.</p>}
        </div>
      )}

      {editing && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="text-lg">
              {editing.id ? "Upravit manuál" : "Nový manuál"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="text-xs font-medium">Slug (URL)</label>
                <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="jak-pridat-podnik" />
              </div>
              <div>
                <label className="text-xs font-medium">Cílová skupina</label>
                <Select value={editing.audience} onValueChange={(v: any) => setEditing({ ...editing, audience: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Veřejné</SelectItem>
                    <SelectItem value="vakant_owners">Jen pro majitele Vacantů</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">Pořadí</label>
                <Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value || "100", 10) })} />
              </div>
              <div>
                <label className="text-xs font-medium">Zdrojový jazyk</label>
                <Select value={sourceLang} onValueChange={(v: any) => setSourceLang(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MANUAL_LANGS.map((l) => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium">Titulek ({sourceLang})</label>
              <Input
                value={editing.title_i18n?.[sourceLang] ?? ""}
                onChange={(e) => setEditing({ ...editing, title_i18n: { ...editing.title_i18n, [sourceLang]: e.target.value } })}
              />
              <label className="text-xs font-medium">Popis ({sourceLang})</label>
              <Textarea
                rows={2}
                value={editing.description_i18n?.[sourceLang] ?? ""}
                onChange={(e) => setEditing({ ...editing, description_i18n: { ...editing.description_i18n, [sourceLang]: e.target.value } })}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium">Kroky ({sourceLang})</label>
                <Button size="sm" variant="ghost" onClick={() => setEditing({
                  ...editing,
                  content_i18n: {
                    ...editing.content_i18n,
                    [sourceLang]: [...(editing.content_i18n?.[sourceLang] ?? []), { title: "", body: "" }],
                  },
                })}><Plus className="w-3 h-3 mr-1" />Přidat krok</Button>
              </div>
              {(editing.content_i18n?.[sourceLang] ?? []).map((step, idx) => (
                <div key={idx} className="border rounded-md p-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono w-6">{idx + 1}.</span>
                    <Input
                      placeholder="Titulek kroku"
                      value={step.title}
                      onChange={(e) => {
                        const arr = [...(editing.content_i18n?.[sourceLang] ?? [])];
                        arr[idx] = { ...arr[idx], title: e.target.value };
                        setEditing({ ...editing, content_i18n: { ...editing.content_i18n, [sourceLang]: arr } });
                      }}
                    />
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => {
                      const arr = (editing.content_i18n?.[sourceLang] ?? []).filter((_, i) => i !== idx);
                      setEditing({ ...editing, content_i18n: { ...editing.content_i18n, [sourceLang]: arr } });
                    }}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                  <Textarea
                    rows={3}
                    placeholder="Popis kroku, instrukce…"
                    value={step.body}
                    onChange={(e) => {
                      const arr = [...(editing.content_i18n?.[sourceLang] ?? [])];
                      arr[idx] = { ...arr[idx], body: e.target.value };
                      setEditing({ ...editing, content_i18n: { ...editing.content_i18n, [sourceLang]: arr } });
                    }}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input
                      placeholder="Route pro screenshot (např. /admin/manualy)"
                      value={(step as any).route ?? ""}
                      onChange={(e) => {
                        const arr = [...(editing.content_i18n?.[sourceLang] ?? [])];
                        arr[idx] = { ...arr[idx], route: e.target.value || undefined } as any;
                        setEditing({ ...editing, content_i18n: { ...editing.content_i18n, [sourceLang]: arr } });
                      }}
                    />
                    <Input
                      placeholder="CSS selektor (volitelně, např. .card)"
                      value={(step as any).selector ?? ""}
                      onChange={(e) => {
                        const arr = [...(editing.content_i18n?.[sourceLang] ?? [])];
                        arr[idx] = { ...arr[idx], selector: e.target.value || undefined } as any;
                        setEditing({ ...editing, content_i18n: { ...editing.content_i18n, [sourceLang]: arr } });
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button onClick={doTranslate} disabled={translating} variant="secondary">
                {translating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
                Přeložit do všech jazyků
              </Button>
              <Button onClick={doSave} disabled={savingRow}>
                {savingRow ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Uložit
              </Button>
              {editing.id ? (
                <Button variant="secondary" onClick={() => doCapture(editing)} disabled={capturing === editing.id}>
                  {capturing === editing.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                  Zachytit screenshoty (všechny jazyky)
                </Button>
              ) : null}
              <Button variant="ghost" onClick={() => setEditing(null)}>Zavřít</Button>
            </div>

            <div className="text-xs text-muted-foreground">
              Přeložené jazyky: {MANUAL_LANGS.map((l) => (
                <span key={l} className={`inline-block mr-1 px-1.5 py-0.5 rounded ${editing.title_i18n?.[l] ? "bg-emerald-500/10 text-emerald-600" : "bg-muted"}`}>{l}</span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!previewFor} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 gap-0 flex flex-col">
          <DialogHeader>
            <div className="p-3 border-b">
              <DialogTitle>
                Náhled PDF — {previewFor ? currentTitle(previewFor) : ""}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Rychlý náhled všech stránek vygenerovaného PDF manuálu s navigací mezi stránkami.
              </DialogDescription>
            </div>
          </DialogHeader>
          {previewFor && (() => {
            const fileLangs = (previewFor.files ?? []).map((f) => f.lang).filter((l) => MANUAL_LANGS.includes(l as ManualLang)) as ManualLang[];
            const contentLangs = Object.keys(previewFor.content_i18n).filter((l) => MANUAL_LANGS.includes(l as ManualLang)) as ManualLang[];
            const availableLangs = fileLangs.length > 0 ? fileLangs : contentLangs;
            return (
              <div className="flex-1 min-h-0 flex flex-col">
                <Tabs value={previewLang} onValueChange={(v) => setPreviewLang(v as ManualLang)} className="border-b px-3 py-2">
                  <TabsList className="flex flex-wrap h-auto">
                    {availableLangs.map((l) => (
                      <TabsTrigger key={l} value={l} className="text-xs">{l.toUpperCase()}</TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
                {previewMeta && !previewMeta.ok && (
                  <div className="px-3 py-2 text-xs text-amber-600 dark:text-amber-400 border-b">
                    Pozor: {previewMeta.missing.length} znaků není plně podporováno fontem
                    {previewMeta.missing.length > 0 && <>: <span className="font-mono">{previewMeta.missing.slice(0, 30).join(" ")}</span></>}
                  </div>
                )}
                <div className="relative flex-1 min-h-0 bg-muted/30">
                  {previewBusy && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/60 z-10">
                      <Loader2 className="animate-spin" />
                    </div>
                  )}
                  {previewUrl && !previewBusy && <PdfAllPages key={previewUrl} url={previewUrl} />}
                </div>
                <div className="flex justify-between items-center gap-2 flex-wrap border-t px-3 py-2">
                  <div className="text-xs text-muted-foreground">
                    {previewUsesStoredFile
                      ? "Náhled zobrazuje uložený PDF soubor včetně všech jeho stránek a obrázků."
                      : "Pracovní náhled se generuje z aktuálního uloženého obsahu. Pro publikaci klikněte na „Vygenerovat PDF“ a poté „Schválit“."}
                  </div>
                  <div className="flex gap-2">
                    {previewUrl && (
                      <a href={previewUrl} download={`cerveza-international-${previewFor.slug}-${previewLang}.pdf`}>
                        <Button variant="outline" size="sm"><FileDown className="w-4 h-4 mr-1" /> Stáhnout náhled</Button>
                      </a>
                    )}
                    <Button size="sm" onClick={closePreview}>Zavřít</Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={suggestOpen} onOpenChange={(o) => { if (!suggestBusy) setSuggestOpen(o); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> Navrhnout strukturu manuálu</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Popiš, co má manuál pokrývat. AI navrhne titulek, popis a jednotlivé kroky podle skutečných obrazovek a rolí v systému.
              Návrh vzniká v jazyce „<b>{sourceLang.toUpperCase()}</b>" a přepíše obsah editoru pro tento jazyk.
            </p>
            <div className="grid gap-3 md:grid-cols-[2fr_1fr]">
              <div>
                <label className="text-xs font-medium">Téma manuálu</label>
                <Textarea
                  rows={4}
                  autoFocus
                  placeholder="Např.: Jak si majitel Vacantu propojí svůj podnik s katalogem, nahraje fotky a spustí platbu za funkce."
                  value={suggestTopic}
                  onChange={(e) => setSuggestTopic(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium">Počet kroků</label>
                <Input
                  type="number"
                  min={3}
                  max={20}
                  value={suggestStepsHint}
                  onChange={(e) => setSuggestStepsHint(Math.max(3, Math.min(20, parseInt(e.target.value || "8", 10))))}
                />
                <p className="mt-1 text-[11px] text-muted-foreground">Doporučeno 5–10.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setSuggestOpen(false)} disabled={suggestBusy}>Zrušit</Button>
              <Button onClick={doSuggest} disabled={suggestBusy || !suggestTopic.trim()}>
                {suggestBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                Vygenerovat návrh
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
